import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Briefcase } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function VendorPortal() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const qc = useQueryClient();
  const { data, isError } = useQuery({
    queryKey: ['vportal', token], enabled: !!token,
    queryFn: () => axios.get(`${API}/procurement/public/vendor?token=${token}`).then((r) => r.data),
  });
  const [f, setF] = useState({ invoice_number: '', amount: '', po_number: '', notes: '' });
  const submit = useMutation({
    mutationFn: () => axios.post(`${API}/procurement/public/submit-invoice`, { vendor_token: token, ...f, amount: Number(f.amount), po_number: f.po_number || null }).then((r) => r.data),
    onSuccess: (d) => { toast.success(d.message || 'Submitted'); setF({ invoice_number: '', amount: '', po_number: '', notes: '' }); qc.invalidateQueries({ queryKey: ['vportal', token] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Submission failed'),
  });

  if (!token) return <div className="min-h-screen flex items-center justify-center text-slate-500">Missing portal token.</div>;
  if (isError) return <div className="min-h-screen flex items-center justify-center text-rose-600" data-testid="vportal-invalid">This vendor portal link is invalid or expired.</div>;

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 py-10 px-4" data-testid="vendor-portal-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-1"><Briefcase className="text-orange-500" size={26} /><h1 className="font-display text-2xl font-extrabold">Vendor Invoice Portal</h1></div>
        <p className="text-sm text-slate-400 mb-6">{data ? `Welcome, ${data.vendor_name}. Submit an invoice against a purchase order.` : 'Loading...'}</p>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 mb-6">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Submit an invoice</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input className="bg-slate-900 border-slate-700" placeholder="Invoice number" value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} data-testid="vp-invoice-number" />
            <Input className="bg-slate-900 border-slate-700" type="number" placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="vp-amount" />
            <select className="bg-slate-900 border border-slate-700 rounded-md h-10 px-2 text-sm" value={f.po_number} onChange={(e) => setF({ ...f, po_number: e.target.value })} data-testid="vp-po">
              <option value="">Against PO (optional)</option>
              {(data?.open_pos || []).map((p) => <option key={p.po_number} value={p.po_number}>{p.po_number} ({money(p.total)})</option>)}
            </select>
            <Input className="bg-slate-900 border-slate-700" placeholder="Notes (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
          <Button className="mt-4 bg-orange-500 hover:bg-orange-600" onClick={() => submit.mutate()} disabled={!f.invoice_number || !f.amount || submit.isPending} data-testid="vp-submit-btn">{submit.isPending ? 'Submitting...' : 'Submit invoice'}</Button>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Your submitted invoices</p>
          {(data?.invoices || []).length === 0 && <p className="text-sm text-slate-500">No invoices yet.</p>}
          {(data?.invoices || []).map((i) => (
            <div key={i.invoice_number} className="flex items-center gap-3 py-2 border-t border-slate-700 text-sm">
              <span className="font-medium">{i.invoice_number}</span>
              {i.po_number && <span className="text-slate-500 text-xs">{i.po_number}</span>}
              <span className="font-bold ml-auto">{money(i.amount)}</span>
              <span className="font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-slate-700 text-slate-300">{i.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
